import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import reviewService from './reviewService';

import type { PaginatedQueryParams, UserFilterParams } from '@/types/api';
import type { CycleInstance } from '@/types/cycle-evaluation/CycleInstance';
import type {
    ReviewAudience,
    ReviewEvaluatorSelection,
    ReviewEvaluatorSelectionUpdate,
} from '@/types/review/ReviewAudience';
import type { ReviewInstance } from '@/types/review/ReviewInstance';

import { queryClient } from '@/AppWithProviders';
import { ReviewMutationKey, ReviewQueryKey } from '@/constants/QueryKeys';
import { useAmelioInfiniteQuery } from '@/hooks/api/useAmelioInfiniteQuery';
import { useAmelioPaginatedQuery } from '@/hooks/api/useAmelioPaginatedQuery';

const useReadyToBeLaunchedReviews = ({
    pageSize,
    options,
}: PaginatedQueryParams<CycleInstance>) => {
    return useAmelioInfiniteQuery<CycleInstance>({
        queryKey: [ReviewQueryKey.READY_TO_BE_LAUNCHED_REVIEWS],
        queryFn: reviewService.getReadyToBeLaunchedReviews,
        pageSize,
        options,
    });
};

const useOngoingReviews = ({ pageSize, options }: PaginatedQueryParams<ReviewInstance>) => {
    return useAmelioInfiniteQuery<ReviewInstance>({
        queryKey: [ReviewQueryKey.ONGOING_REVIEWS],
        queryFn: reviewService.getOngoingReviews,
        pageSize,
        options,
    });
};

const usePastReviews = ({ pageSize, options }: PaginatedQueryParams<ReviewInstance>) => {
    return useAmelioInfiniteQuery<ReviewInstance>({
        queryKey: [ReviewQueryKey.PAST_REVIEWS],
        queryFn: reviewService.getPastReviews,
        pageSize,
        options,
    });
};

const useReadyToBeLaunchedReviewsPaged = ({
    pageNumber,
    pageSize,
    filters,
}: {
    pageNumber: number;
    pageSize: number;
    filters?: UserFilterParams;
}) => {
    return useAmelioPaginatedQuery<CycleInstance>({
        queryKey: [ReviewQueryKey.READY_TO_BE_LAUNCHED_REVIEWS_PAGED],
        queryFn: reviewService.getReadyToBeLaunchedReviews,
        pageNumber,
        pageSize,
        filters,
        options: { placeholderData: keepPreviousData },
    });
};

const useOngoingReviewsPaged = ({
    pageNumber,
    pageSize,
    filters,
}: {
    pageNumber: number;
    pageSize: number;
    filters?: UserFilterParams;
}) => {
    return useAmelioPaginatedQuery<ReviewInstance>({
        queryKey: [ReviewQueryKey.ONGOING_REVIEWS_PAGED],
        queryFn: reviewService.getOngoingReviews,
        pageNumber,
        pageSize,
        filters,
        options: { placeholderData: keepPreviousData },
    });
};

const usePastReviewsPaged = ({
    pageNumber,
    pageSize,
    filters,
}: {
    pageNumber: number;
    pageSize: number;
    filters?: UserFilterParams;
}) => {
    return useAmelioPaginatedQuery<ReviewInstance>({
        queryKey: [ReviewQueryKey.PAST_REVIEWS_PAGED],
        queryFn: reviewService.getPastReviews,
        pageNumber,
        pageSize,
        filters,
        options: { placeholderData: keepPreviousData },
    });
};

const useUpdateReviewAudience = () => {
    return useMutation({
        mutationKey: [ReviewMutationKey.UPDATE_REVIEW_AUDIENCE],
        mutationFn: reviewService.updateReviewAudience,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.CYCLE_INSTANCE],
            });
        },
    });
};

const useGetReviewAudience = ({ reviewAudienceId }: { reviewAudienceId?: string }) => {
    return useQuery<ReviewAudience>({
        queryKey: [ReviewQueryKey.REVIEW_AUDIENCE, reviewAudienceId],
        queryFn: () => reviewService.getReviewAudience(reviewAudienceId!),
        enabled: !!reviewAudienceId,
    });
};

const useUpdateReviewEvaluatorSelection = () => {
    return useMutation({
        mutationKey: [ReviewMutationKey.UPDATE_REVIEW_EVALUATOR_SELECTION],
        mutationFn: (payload: ReviewEvaluatorSelectionUpdate) =>
            reviewService.updateReviewEvaluatorSelection(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.CYCLE_INSTANCE],
            });
        },
    });
};

const useGetReviewEvaluatorSelection = (reviewEvaluatorSelectionId?: string) => {
    return useQuery<ReviewEvaluatorSelection>({
        queryKey: [ReviewQueryKey.REVIEW_AUDIENCE, reviewEvaluatorSelectionId],
        queryFn: () => reviewService.getReviewEvaluatorSelection(reviewEvaluatorSelectionId!),
        enabled: !!reviewEvaluatorSelectionId,
    });
};

export {
    useReadyToBeLaunchedReviews,
    useOngoingReviews,
    usePastReviews,
    useReadyToBeLaunchedReviewsPaged,
    useOngoingReviewsPaged,
    usePastReviewsPaged,
    useUpdateReviewAudience,
    useGetReviewAudience,
    useUpdateReviewEvaluatorSelection,
    useGetReviewEvaluatorSelection,
};
